/**
 * fund.ts
 *
 * TypeScript types for the WhatsTheMoat mutual fund feature.
 *
 * PreCalculatedFundData is the canonical shape sent to Claude — EVERY number
 * is computed before the API call so Claude writes analysis, not arithmetic.
 */

/* ------------------------------------------------------------------ */
/*  Primitives                                                        */
/* ------------------------------------------------------------------ */

export interface NavPoint {
  date: string; // YYYY-MM-DD
  nav: number;
}

export interface FundHolding {
  name: string;
  percentage: number;
  sector: string;
  monthChangePct?: number | null; // month-over-month drift from mfdata
}

export interface FundSectorAllocation {
  sector: string;
  percentage: number;
}

/* ------------------------------------------------------------------ */
/*  Pre-calculated data package sent to Claude                        */
/* ------------------------------------------------------------------ */

export interface PreCalculatedFundData {
  // ── Identity / metadata ────────────────────────────────────────
  schemeCode: string;
  isin: string | null;
  name: string;
  amc: string;
  category: string;       // SEBI category (e.g., "Large Cap", "Liquid Funds")
  fundType: string;       // "Equity" | "Debt" | "Hybrid" | "Index" | "ELSS" | "Other"
  schemeType: string;     // "Open Ended" | "Close Ended"
  planType: string;       // "Direct" | "Regular"
  isDirect: boolean;
  benchmark: string | null;
  riskLabel: string | null; // SEBI risk-o-meter: "Very High Risk" | "High Risk" | etc.

  // ── NAV ────────────────────────────────────────────────────────
  nav: number;
  navDate: string;        // YYYY-MM-DD
  aumCr: number | null;   // in crores (converted from paise)
  morningstarRating: number | null; // 1-5

  // ── Age ────────────────────────────────────────────────────────
  launchDate: string | null; // YYYY-MM-DD
  fundAgeYears: number | null;
  fundAgeText: string;       // "8 years 3 months" or "Data not available"

  // ── Cost (pre-computed ₹-on-₹1-lakh figures) ──────────────────
  cost: {
    expenseRatio: number | null;             // %
    expensePerLakhAnnual: number | null;     // ₹450 on ₹1L @ 0.45%
    expensePerLakhMonthly: number | null;
    regularPlanExpenseRatio: number | null;
    regularPlanExpensePerLakh: number | null;
    directVsRegularSavingAnnual: number | null;   // ₹/year per lakh
    directVsRegularTenYearImpact: number | null;  // compounded on ₹10L over 10yr
    categoryAvgExpenseRatio: number | null;
    categoryAvgExpensePerLakh: number | null;
    expenseSavingVsCategory: number | null;       // ₹/year per lakh
    exitLoad: string | null;              // raw string from API (varies)
    minSip: number | null;
    minLumpsum: number | null;
    minAdditional: number | null;
  };

  // ── Returns (from mfdata + calculated ₹1 lakh impact) ─────────
  returns: {
    // Pass-through from mfdata.in (already computed upstream)
    oneMonth: number | null;   // decimals (0.12 = 12%)
    threeMonth: number | null;
    sixMonth: number | null;
    oneYear: number | null;
    threeYearCagr: number | null;
    fiveYearCagr: number | null;
    sinceInceptionCagr: number | null;

    // ₹1 lakh impact (THE headline numbers for Simple English)
    lakhOneYearValue: number | null;
    lakhOneYearProfit: number | null;
    lakhThreeYearValue: number | null;
    lakhThreeYearProfit: number | null;
    lakhFiveYearValue: number | null;
    lakhFiveYearProfit: number | null;

    // Rolling 3-year CAGR (calculated from daily NAV)
    rollingThreeYearBest: number | null;
    rollingThreeYearWorst: number | null;
    rollingThreeYearMedian: number | null;
    rollingThreeYearPositivePct: number | null;
    rollingThreeYearDistribution: Array<{ range: string; count: number }>;

    // Rolling 5-year CAGR
    rollingFiveYearBest: number | null;
    rollingFiveYearWorst: number | null;
    rollingFiveYearMedian: number | null;

    // Year-by-year calendar returns (last 5 years)
    yearlyReturns: Array<{ year: number; returnPct: number }>;

    // SIP simulation (₹10k/month)
    sipTenKThreeYearInvested: number | null;
    sipTenKThreeYearValue: number | null;
    sipTenKThreeYearProfit: number | null;
    sipTenKThreeYearXirr: number | null;
    sipTenKThreeYearMonthly: Array<{ month: string; invested: number; value: number }>;
    sipTenKFiveYearInvested: number | null;
    sipTenKFiveYearValue: number | null;
    sipTenKFiveYearProfit: number | null;
    sipTenKFiveYearXirr: number | null;
    sipTenKFiveYearMonthly: Array<{ month: string; invested: number; value: number }>;

    // Category context (bootstrap hardcoded → DB-computed as DB grows)
    categoryAvgOneYear: number | null;
    categoryAvgThreeYear: number | null;
  };

  // ── Risk (from mfdata pass-through + calculated drawdown) ─────
  risk: {
    standardDeviation: number | null;   // annualized, %
    sharpeRatio: number | null;
    sortinoRatio: number | null;
    beta: number | null;
    rSquared: number | null;
    jensensAlpha: number | null;
    treynorRatio: number | null;
    informationRatio: number | null;

    maxDrawdownPct: number | null;         // e.g. -32.4
    maxDrawdownLakhValue: number | null;   // ₹68,000 after -32% drawdown
    maxDrawdownPeakDate: string | null;
    maxDrawdownTroughDate: string | null;
    maxDrawdownRecoveryMonths: number | null;

    worstMonthReturnPct: number | null;
    worstMonthLakhValue: number | null;
    worstThreeMonthReturnPct: number | null;

    volatilityScale1to10: number | null;   // our simplified scale

    // Category averages (from mfdata)
    categoryAvgSharpe: number | null;
    categoryAvgBeta: number | null;
  };

  // ── Holdings ──────────────────────────────────────────────────
  holdings: {
    totalStocks: number;
    top10: FundHolding[];
    top5ConcentrationPct: number | null;
    top10ConcentrationPct: number | null;
    sectorAllocation: FundSectorAllocation[];
    equityPct: number | null;
    debtPct: number | null;
    otherPct: number | null;
  } | null;

  // ── Full NAV series for chart rendering (daily) ───────────────
  navHistory: NavPoint[];

  // ── Recent news (Perplexity, last 12 months) ──────────────────
  // Fetched for ALL tiers so FREE→PRO upgrade reuses the same frozen
  // snapshot instead of hitting Perplexity again. null = fetch failed
  // or no news found — the prompt handles this gracefully.
  recentNews: {
    news: Array<{
      headline: string;
      date: string;
      source: string;
      url: string;
      summary: string;
      impact: "positive" | "negative" | "neutral";
    }>;
    fetchedAt: string;
  } | null;

  // ── Meta (used by prompt "data availability" notes) ───────────
  dataAvailability: {
    hasNavHistory: boolean;
    hasHoldings: boolean;
    hasReturns: boolean;
    hasRiskRatios: boolean;
    hasDirectVsRegular: boolean;
    hasCategoryAverages: boolean;
    hasRecentNews: boolean;
  };
}

/* ------------------------------------------------------------------ */
/*  AI report output types                                            */
/* ------------------------------------------------------------------ */

export interface FundReportSection {
  financial: string;
  simple: string;
  [key: string]: unknown;
}

export interface FundScoreBreakdown {
  costEfficiency: { label: string; score: number; reasoning: string };
  performanceConsistency: { label: string; score: number; reasoning: string };
  riskManagement: { label: string; score: number; reasoning: string };
  fundManagerQuality: { label: string; score: number; reasoning: string };
  portfolioQuality: { label: string; score: number; reasoning: string };
  transparency: { label: string; score: number; reasoning: string };
}

export interface FundReportContent {
  // Sections 1-5 (FREE)
  fundSnapshot?: FundReportSection;
  costBreakdown?: FundReportSection;
  performanceAnalysis?: FundReportSection;
  portfolioHoldings?: FundReportSection & {
    topHoldings?: FundHolding[];
    sectorAllocation?: FundSectorAllocation[];
  };
  fundManager?: FundReportSection;

  // Sections 6-8 (PRO)
  riskAnalysis?: FundReportSection;
  wtmAssessment?: FundReportSection & {
    overallScore?: number;
    subScores?: FundScoreBreakdown;
  };
  newsChanges?: FundReportSection;

  // Section 9 (TAX) — generated for all tiers
  taxInfo?: FundReportSection;

  summary?: string;
}

export type FundReportTier = "free" | "pro" | "upgrade";

/* ------------------------------------------------------------------ */
/*  Section ordering & metadata                                       */
/* ------------------------------------------------------------------ */

export const FUND_SECTION_ORDER = [
  "fundSnapshot",
  "costBreakdown",
  "performanceAnalysis",
  "portfolioHoldings",
  "fundManager",
  "riskAnalysis",
  "wtmAssessment",
  "newsChanges",
  "taxInfo",
] as const;

export type FundSectionKey = (typeof FUND_SECTION_ORDER)[number];

export const FUND_SECTION_META: {
  key: FundSectionKey;
  title: string;
  icon: string;
  isFree: boolean;
}[] = [
  { key: "fundSnapshot", title: "Fund Snapshot", icon: "📋", isFree: true },
  { key: "costBreakdown", title: "Cost Breakdown", icon: "💰", isFree: true },
  { key: "performanceAnalysis", title: "Performance Analysis", icon: "📈", isFree: true },
  { key: "portfolioHoldings", title: "Portfolio Holdings", icon: "🏦", isFree: true },
  { key: "fundManager", title: "Fund Manager", icon: "👤", isFree: true },
  { key: "riskAnalysis", title: "Risk Analysis", icon: "⚠️", isFree: false },
  { key: "wtmAssessment", title: "WTM Assessment", icon: "🎯", isFree: false },
  { key: "newsChanges", title: "News & Changes", icon: "📰", isFree: false },
  { key: "taxInfo", title: "Tax & Practical Info", icon: "🧾", isFree: true },
];

export const FREE_TIER_SECTIONS: FundSectionKey[] = [
  "fundSnapshot",
  "costBreakdown",
  "performanceAnalysis",
  "portfolioHoldings",
  "fundManager",
  "taxInfo",
];

export const PRO_TIER_SECTIONS: FundSectionKey[] = [
  ...FREE_TIER_SECTIONS,
  "riskAnalysis",
  "wtmAssessment",
  "newsChanges",
];

export const UPGRADE_SECTIONS: FundSectionKey[] = [
  "riskAnalysis",
  "wtmAssessment",
  "newsChanges",
];
